import React, { useState, useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";

const styles = {
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "60vh",
    backgroundColor: "transparent",
    position: "relative",
    overflow: "hidden",
  },
  loader: {
    backgroundColor: "grey",
    border: "8px solid #e9a132",
    borderRadius: "50%",
    width: "80px",
    height: "80px",
    animation:
      "spin 2s linear infinite, glow 1.5s ease-in-out infinite, pulse 2s ease-in-out infinite",
  },
  quote: {
    marginTop: "40px",
    fontSize: "18px",
    fontWeight: "bold",
    textAlign: "center",
    color: "#e9a132",
    opacity: 0,
    fontFamily: "Orbitron, sans-serif",
    width: "100vw",
  },
  body: {
    background: "none",
  },
  load: {
    position: "absolute",
    width: "1000px",
    height: "50px",
    left: "40%",
    top: "45%",
    marginLeft: "-350px",
    overflow: "visible",
    userSelect: "none",
    cursor: "default",
  },
  loadDiv: {
    position: "absolute",
    width: "50px",
    height: "70px",
    fontSize: "1.4rem", // Increase font size
    opacity: 0,
    fontFamily: "Helvetica, Arial, sans-serif",
    animation: "move 2.2s linear infinite",
    transform: "rotate(180deg)",
    color: "#e9a132",
  },
  keyframes: `
    @keyframes move {
      0% { left: 0; opacity: 0; }
      35% { left: 41%; transform: rotate(0deg); opacity: 1; }
      65% { left: 59%; transform: rotate(0deg); opacity: 1; }
      100% { left: 100%; transform: rotate(-180deg); opacity: 0; }
    }
    @keyframes moveEndPause {
      0%, 90% { left: 100%; opacity: 0; }
      100% { left: 0; opacity: 0; }
    }
  `,
};

const LoadingIndicator = () => {
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={styles.body}
    >
      <CircularProgress />
    </Box>
  );
};

const GenerateLoadingIndicator = () => {
  const quotes = useMemo(() => [
    "Please wait, Generating your Response...",
    "Initializing AI system...",
    "Processing data...",
    "Analyzing inputs...",
    "Compiling results...",
    "Optimizing performance...",
    "Applying configurations...",
    "Finalizing setup...",
    "Almost there...",
    "Preparing your experience...",
  ], []);

  const [currentQuote, setCurrentQuote] = useState(quotes[0]);
  const [quoteAnimation, setQuoteAnimation] = useState("fadeIn");

  useEffect(() => {
    const interval = setInterval(() => {
      setQuoteAnimation("fadeOut"); 
      setTimeout(() => {
        setCurrentQuote((prevQuote) => {
          const currentIndex = quotes.indexOf(prevQuote);
          return quotes[(currentIndex + 1) % quotes.length];
        });
        setQuoteAnimation("fadeIn"); 
      }, 1000); 
    }, 3500);

    return () => clearInterval(interval); 
  }, [quotes]);

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }

      @keyframes glow {
        0% { box-shadow: 0 0 5px #e9a132, 0 0 10px #e9a132, 0 0 15px #e9a132; }
        50% { box-shadow: 0 0 20px #e9a132, 0 0 40px #e9a132, 0 0 60px #e9a132; }
        100% { box-shadow: 0 0 5px #e9a132, 0 0 10px #e9a132, 0 0 15px #e9a132; }
      }

      @keyframes pulse {
        0% { transform: scale(1); opacity: 0.8; }
        50% { transform: scale(1.1); opacity: 1; }
        100% { transform: scale(1); opacity: 0.8; }
      }

      @keyframes fadeIn {
        0% { opacity: 0; transform: translateY(-20px); }
        50% { opacity: 0.5; transform: translateY(0); }
        100% { opacity: 1; transform: translateY(20px); }
      }

      @keyframes fadeOut {
        100% { opacity: 0; transform: translateY(40px); }
        0% { opacity: 1; transform: translateY(20px); }
      }

      /* Import a futuristic font like Orbitron */
      @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@500&display=swap');
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div>
      <div style={styles.loadingContainer}>
        <div style={styles.loader}></div>
        <div
          style={{
            ...styles.quote,
            animation: `${quoteAnimation} 1s ease-out forwards`,
          }}
        >
          {currentQuote}
        </div>
      </div>
    </div>
  );
};

export { LoadingIndicator, GenerateLoadingIndicator };